import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageWrapper from "../SignupPages/PageWrapper";
import { Radio } from "@mui/material";
import "./NewSignupPageCommon.css";
import "../SignupPages/SignupCommon.css";
import Alert from "../../assets/images/alert.png";
import CustomStepper from "./Stepper";
import { useLocation } from "react-router-dom";

const NewSignupPage1: React.FC = () => {
  const [gender, setGender] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [isValidAge, setIsValidAge] = useState<boolean>(true);
  const [isInvalidDate, setIsInvalidDate] = useState<boolean>(false);
  const [isPlanningBaby, setIsPlanningBaby] = useState<string>("");
  const navigate = useNavigate();

  const genderOptions = ["Male", "Female"];
  const optionsForBabyPlanning = [
    "Currently or possibly pregnant, or actively trying to become pregnant",
    "Breastfeeding or bottle feeding with breastmilk",
    "None of the Following",
  ];

  const location = useLocation();

const safeNavigate = (path: string) => {
  if (location.pathname !== path) {
    navigate(path, { replace: true });
  }
};


useEffect(() => {
  const step1Completed = localStorage.getItem("step-1-completed");
  if (step1Completed) {
    safeNavigate("/getting-started/step-1");
  }
}, []);


  const calculateAge = (dob: string) => {
    const [month, day, year] = dob.split("/").map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const isValidDate = (dob: string) => {
    const [month, day, year] = dob.split("/").map(Number); // MM/DD/YYYY format

    // Check if the month, day, or year are invalid
    if (
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      year < 1900 ||
      year > new Date().getFullYear()
    ) {
      setIsInvalidDate(true);
      return false;
    }

    // Create a date object to check for valid day/month combinations
    const birthDate = new Date(year, month - 1, day);

    // Check if the date is valid
    if (
      birthDate.getDate() !== day ||
      birthDate.getMonth() !== month - 1 ||
      birthDate.getFullYear() !== year
    ) {
      setIsInvalidDate(true);
      return false;
    }

    // Check if the date is in the future
    const today = new Date();
    if (birthDate > today) {
      setIsInvalidDate(true);
      return false;
    }

    setIsInvalidDate(false);
    return true;
  };

  useEffect(() => {
    const savedGender = localStorage.getItem("gender");
    const savedIsPlanningBaby = localStorage.getItem("isPlanningBaby");
    const savedDob = localStorage.getItem("dob");

    if (savedGender) {
      const parsedGender = JSON.parse(savedGender);
      setGender(parsedGender.value);
    }

    if (savedIsPlanningBaby) {
      const parsedIsPlanningBaby = JSON.parse(savedIsPlanningBaby);
      setIsPlanningBaby(parsedIsPlanningBaby.value);
    }

    if (savedDob) {
      const parsedDob = JSON.parse(savedDob);
      setDob(parsedDob.value);
    }
  }, []);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value.replace(/[^\d]/g, "");

    if (value.length > 8) value = value.slice(0, 8);

    let formattedValue = value;
    if (value.length >= 2)
      formattedValue = `${value.slice(0, 2)}/${value.slice(2)}`;
    if (value.length >= 4)
      formattedValue = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(
        4
      )}`;

    setDob(formattedValue);

    if (formattedValue.length === 10) {
      // Validate the date before calculating age
      const isDateValid = isValidDate(formattedValue);
      if (isDateValid) {
        const age = calculateAge(formattedValue);
        setIsValidAge(age >= 18 && age <= 74);
      } else {
        setIsValidAge(false);
      }
    } else {
      setIsValidAge(true);
    }
  };

  const handleGenderOptionClick = (option: string) => {
    setGender(option);
    if (option === "Male") {
      setIsPlanningBaby("");
    }
  };

  const handleBabyPlanningOptionClick = (option: string) => {
    setIsPlanningBaby(option);
  };

  const styles = {
    optionsStacked: {
      display: "flex",
      flexDirection: "column" as const,
      gap: "15px",
      marginBottom: "20px",
      width: "100%",
      marginTop: "20px",
    },
    optionLabel: {
      fontSize: "18px",
      padding: "15px",
      cursor: "pointer",
      border: "1px solid #96C6DA",
      borderRadius: "10px",
      transition: "background-color 0.3s ease",
      width: "100%",
      textAlign: "center" as const,
      backgroundColor: "white",
    },
    optionLabelSelected: {
      backgroundColor: "#0C3972",
      color: "white",
    },
  };

  const handleNext = () => {
    if (gender) {
      localStorage.setItem(
        "gender",
        JSON.stringify({ key: "gender", value: gender, id: 18026054 })
      );
    }

    if (isPlanningBaby) {
      localStorage.setItem(
        "isPlanningBaby",
        JSON.stringify({
          key: "isPlanningBaby",
          value: isPlanningBaby,
          id: 18026056,
        })
      );
    }

    if (dob) {
      // Store the date in MM/DD/YYYY format in local storage
      localStorage.setItem(
        "dob",
        JSON.stringify({ key: "dob", value: dob, id: 18026057 })
      );
    }

    localStorage.setItem("step-1-completed", "true");
  };

  const nextPage =
    gender === "Male" ||
    (gender === "Female" && isPlanningBaby === "None of the Following")
      ? "/getting-started/step-2"
      : "/getting-started/invalid-age";


    const handleBack = () => {
      navigate("/")
        }

  return (
    <PageWrapper onBack={handleBack}>
      <div className="signup-page-container container">
        <CustomStepper activeStep={0} onBack={handleBack}/>
        <div className="signup-content">
          <p className="signup-content-header">Tell us about yourself</p>

          {/* Date of birth */}
          <p className="signup-content-heading signup-content-heading-font-weight">
            What is your date of birth? *
          </p>
          <p className="signup-content-heading-description">(helps the doctor to check for eligibility)</p>
          <div
            className="dob-input-container"
            style={{ marginTop: "15px", height: "52px" }}
          >
            <input
              type="text"
              placeholder="MM/DD/YYYY"
              value={dob}
              onChange={handleDateChange}
              maxLength={10}
              style={{ height: "52px" }}
              className={`input-fields input-box ${
                isInvalidDate || (!isValidAge && dob.length === 10)
                  ? "invalid-input"
                  : ""
              }`}
            />
          </div>
          {isInvalidDate && (
            <div
              className="bmi-error-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                height: "50px",
                color: "#f8454a",
                borderRadius: "48px",
                marginTop: "10px",
                marginBottom: "20px",
              }}
            >
              <img src={Alert} width="16px" height="16px" />
              <p
                className="bmi-content"
                style={{ color: "#f8454a", lineHeight: "18px" }}
              >
                Invalid date of birth. Please enter a valid date.
              </p>
            </div>
          )}

          {!isInvalidDate && !isValidAge && dob.length === 10 && (
            <div
              className="bmi-error-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                padding: "10px",
                height: "50px",
                color: "#f8454a",
                borderRadius: "48px",
                marginTop: "10px",
                marginBottom: "20px",
              }}
            >
              <img src={Alert} width="16px" height="16px" />
              <p
                className="bmi-content"
                style={{ color: "#f8454a", lineHeight: "18px" }}
              >
                You must be between 18 and 74 years old to continue.
              </p>
            </div>
          )}

          {/* Gender */}
          <p className="signup-content-heading signup-content-heading-font-weight" style={{ marginTop: "20px" }}>
            Sex assigned at birth *
          </p>
          <div className="radio-button-group row-buttons" style={{ marginTop: "15px" }}>
            {genderOptions.map((option) => (
              <label
                key={option}
                className={`custom-radio-button custom-radio-row-button  ${
                  gender === option ? "selected" : ""
                }`}
              >
                <Radio
                  disableTouchRipple
                  disableRipple
                  sx={{
                    "&.MuiRadio-colorPrimary": {
                      color: "#D7D7D7",
                    },

                    "&.Mui-checked": {
                      color: "#FFFFFF",
                    },
                  }}
                  name="gender"
                  value={option}
                  checked={gender === option}
                  onChange={() => handleGenderOptionClick(option)}
                />
                {option}
              </label>
            ))}
          </div>

          {/* Baby Planning */}
          {gender === "Female" && (
            <>
              <p className="signup-content-heading signup-content-heading-font-weight" style={{ lineHeight: "normal"}}>
                Are you currently or planning for any of the following?
              </p>
              <div style={styles.optionsStacked}>
                {optionsForBabyPlanning.map((option) => (
                  <label
                    key={option}
                    onClick={() => handleBabyPlanningOptionClick(option)}
                    className={`otherOptionLabel ${
                      isPlanningBaby === option ? "optionLabelSelected" : ""
                    }`}
                  >
                    {option}
                  </label>
                ))}
              </div>
            </>
          )}

          {/* Next Button */}
          <Link to={nextPage}>
            <button
              className="next-btn sticky-btn"
              onClick={handleNext}
              disabled={
                !gender ||
                dob.length !== 10 ||
                isInvalidDate ||
                !isValidAge ||
                (gender === "Female" && !isPlanningBaby)
              }
            >
              Next →
            </button>
          </Link>
        </div>
      </div>
    </PageWrapper>
  );
};

export default NewSignupPage1;
